import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { localizedName } from '@/lib/localized-name';
import { useMe } from '@/lib/session';
import { cn } from '@/lib/utils';

/**
 * Wave 4C — the AR/EN description toggle on the document grids.
 *
 * A line's description is filled from the item when it is picked (or
 * imported, or pasted). This decides WHICH name fills it: the English one or
 * the Arabic twin (`localizedName` — the Arabic name when the item has one,
 * else the English). It only changes what NEW fills write; a description
 * already on a line is somebody's text and is never rewritten (BR-MD-03-14's
 * spirit — the same reason a pick does not overwrite typed work).
 *
 * The default is the reader's UI language, and the choice is remembered per
 * user on this device (`localStorage`, wrapped — a private window or blocked
 * storage simply falls back to the default).
 */
export type DescriptionLang = 'en' | 'ar';

const storageKey = (userId: string | undefined) => `palaccount.lineDescriptionLang.${userId ?? 'anon'}`;

function readStored(key: string): DescriptionLang | null {
  try {
    const raw = localStorage.getItem(key);
    return raw === 'en' || raw === 'ar' ? raw : null;
  } catch {
    return null;
  }
}

export interface DescriptionLanguage {
  lang: DescriptionLang;
  setLang: (lang: DescriptionLang) => void;
  /** The item's name in the chosen language. */
  describe: (item: { name: string; nameAr?: string | null | undefined }) => string;
}

export function useDescriptionLanguage(): DescriptionLanguage {
  const { i18n } = useTranslation();
  const { data: me } = useMe();
  const userId = me?.user.id;
  const uiLang: DescriptionLang = i18n.language.startsWith('ar') ? 'ar' : 'en';
  // The user's pick, held per user: a later sign-in as somebody else reads theirs.
  const [picked, setPicked] = useState<{ userId: string | undefined; lang: DescriptionLang } | null>(null);
  const stored = picked && picked.userId === userId ? picked.lang : readStored(storageKey(userId));
  const lang = stored ?? uiLang;

  const setLang = useCallback(
    (next: DescriptionLang) => {
      setPicked({ userId, lang: next });
      try {
        localStorage.setItem(storageKey(userId), next);
      } catch {
        // Storage refused: the choice lasts for this screen only.
      }
    },
    [userId],
  );

  const describe = useCallback(
    (item: { name: string; nameAr?: string | null | undefined }) => localizedName(item, lang),
    [lang],
  );
  return { lang, setLang, describe };
}

/**
 * The E / ع pair in the grid's header. Two pressed-state buttons in a named
 * group, so a screen reader hears "Description language, English, pressed".
 */
export function DescriptionLangToggle({ language }: { language: DescriptionLanguage }) {
  const { t } = useTranslation();
  const options: Array<{ value: DescriptionLang; glyph: string; label: string }> = [
    { value: 'en', glyph: 'E', label: t('lines.descLangEn') },
    { value: 'ar', glyph: 'ع', label: t('lines.descLangAr') },
  ];
  return (
    <span
      role="group"
      aria-label={t('lines.descLang')}
      title={t('lines.descLangHint')}
      className="ms-2 inline-flex overflow-hidden rounded-md border border-input align-middle"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-label={option.label}
          aria-pressed={language.lang === option.value}
          onClick={() => language.setLang(option.value)}
          className={cn(
            'px-1.5 text-xs font-semibold leading-5',
            language.lang === option.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-background text-muted-foreground hover:bg-muted',
          )}
        >
          {option.glyph}
        </button>
      ))}
    </span>
  );
}
